<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Custom Page' : 'Add Custom Page'"
      :breadcrumb="breadcrumb"
      :container-class="null"
      class="mb-32pt"
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group
        :label="$t('schoolMsgs.school')"
        label-for="school"
        label-class="form-label"
        v-if="get(getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN"
      >
        <v-select
          id="school"
          class="form-control v-select-custom"
          label="name"
          v-model="school"
          :reduce="school => school.id"
          placeholder="Select School"
          :options="allSchools"
          :loading="areSchoolsLoading"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!school" v-bind="attributes" v-on="events" />
          </template>

          <template slot="option" slot-scope="option">
            <div class="d-flex align-items-center">
              <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                <b-img :src="option.logo_url" class="img-fluid" width="20" alt="Logo" v-if="option.logo_url" />
                <i class="fas fa-university fa-lg" v-else></i>
              </fmv-avatar>
              <span>{{ option.name }}</span>
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ option.name }}
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.school.required && $v.school.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <page-separator title="Custom Page Details" />

      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="customPage.isProgramPage" name="is-program-page" switch>
          Program Page
        </b-form-checkbox>
        <small class="text-muted">Toggle to specify if this is a program-specific page or an independent custom page</small>
      </b-form-group>

      <b-form-group
        label="Program Category"
        label-for="program-category"
        label-class="form-label"
        v-if="customPage.isProgramPage"
      >
        <v-select
          id="program-category"
          class="form-control v-select-custom"
          label="title"
          v-model="customPage.programCategory"
          :reduce="program => program.id"
          placeholder="Select program category"
          :options="transProgramsOptions"
          :loading="areTransProgramsLoading"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!customPage.programCategory && customPage.isProgramPage" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
        <b-form-invalid-feedback :state="customPage.isProgramPage && !$v.customPage.programCategory.required && $v.customPage.programCategory.$dirty ? false : null"
          >This field is required for program pages.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group label="Navbar Title" label-for="nav-title" label-class="form-label">
        <b-form-input id="nav-title" placeholder="Enter nav title" v-model="customPage.navTitle" required />
      </b-form-group>

      <b-form-group label="Image" label-for="image" label-class="form-label" class="row-align-items-center">
        <b-media class="align-items-center" vertical-align="center">
          <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
            <span v-if="isLogoUploading">...</span>
            <b-img
              :src="customPage.image_url"
              class="img-fluid"
              width="40"
              alt="Logo"
              v-else-if="customPage.image_url"
            />
            <i class="material-icons" v-else>vignette</i>
          </fmv-avatar>

          <image-uploader
            @image="e => setImage(e, 'custom_page_images')"
            :isUploading="isLogoUploading"
            :isRequired="!customPage.image_url"
            placeholder="Select Image"
          />
        </b-media>
      </b-form-group>

      <b-form-group label="Page Title" label-for="title" label-class="form-label">
        <b-form-input id="title" placeholder="Enter title" v-model="customPage.title" required />
      </b-form-group>

      <b-form-group label="Details" label-for="description" label-class="form-label">
        <editor
          :init="{
            height: 400,
            menubar: 'edit view insert format tools table help',
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount'
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
           alignleft aligncenter alignright alignjustify | \
           bullist numlist outdent indent | removeformat | help'
          }"
          v-model="customPage.description"
          placeholder="Enter details"
        />
      </b-form-group>

      <b-form-group label="CTA Button Title" label-for="title" label-class="form-label">
        <b-form-input
          id="cta_title"
          placeholder="Enter CTA Button Title"
          v-model="customPage.ctaTitle"
          :required="!customPage.isProgramPage"
        />
      </b-form-group>

      <b-form-group label="CTA Button Link" label-for="link" label-class="form-label">
        <b-form-input
          id="cta_title"
          placeholder="Enter CTA Button Link"
          v-model="customPage.ctaLink"
          :required="!customPage.isProgramPage"
        />
      </b-form-group>

      <page-separator>
        Custom Page Layout
      </page-separator>
      <b-form-group label="Short Description" label-for="short-description" label-class="form-label">
        <b-form-textarea
          id="short-description"
          placeholder="Enter a description for the custom page"
          :rows="5"
          v-model="customPage.shortDesc"
          :required="customPage.layout === LAYOUT_OPTIONS.LAYOUT_2"
        />
      </b-form-group>
      <small v-if="customPage.layout === LAYOUT_OPTIONS.LAYOUT_1" class="text-muted mb-2"
        >Button will be displayed at the bottom of custom page in this layout.
        <small v-if="customPage.isProgramPage"
          >If no cta link provided, button will redirect to enrollment portal with the 'program category' filter
          applied.</small
        ></small
      >
      <b-form-group label="Banner Layout" label-for="prog_layout" label-class="form-label">
        <div class="row">
          <div class="col-md-6" v-for="layout in layoutOptions" :key="layout.image">
            <div class="layout-hover column text-center clickable-item">
              <figure>
                <img
                  width="100%"
                  height="150px"
                  :src="getImgUrl(layout.image)"
                  @click.prevent="() => (customPage.layout = layout.value)"
                  :alt="heroLayout"
                  :class="heroLayout === layout.value ? 'border-outline-primary' : ''"
                  :style="
                    heroLayout === layout.value
                      ? 'object-fit: cover;'
                      : 'border: #f2f2f2 2px solid; border-radius: 10px; object-fit: cover;'
                  "
                />
              </figure>
            </div>
          </div>
        </div>
      </b-form-group>

      <page-separator>
        Meta
        <md-icon
          style="font-size: 20px; color: grey"
          class="svg-icon mr-2"
          v-b-popover.hover.right
          title="Will be used for SEO purposes"
          >info</md-icon
        >
      </page-separator>
      <b-form-group label="Title" label-for="meta-title" label-class="form-label">
        <b-form-textarea
          id="meta-title"
          placeholder="Enter title"
          rows="3"
          maxlength="200"
          v-model="customPage.metaTitle"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group label="Description" label-for="meta-desc" label-class="form-label">
        <b-form-textarea
          id="meta-desc"
          placeholder="Enter description"
          rows="3"
          maxlength="200"
          v-model="customPage.metaDesc"
        ></b-form-textarea>
      </b-form-group>

      <page-separator>Page Style</page-separator>
      <section-style-panel
        v-model="pageStyle"
        :capabilities="PAGE_STYLE_CAPABILITIES.custom"
        :organization="selectedOrganization"
      />

      <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? 'Update' : 'Post' }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import ImageUploader from '@/components/ImageUploader.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import Editor from '@tinymce/tinymce-vue';
import axios from 'axios';
import { FmvAvatar } from 'fmv-avatar';
import { get } from 'lodash';
import vSelect from 'vue-select';
import { requiredIf } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';
import { SCHOOL_PAYMENT_PLANS, USER_ROLES } from '../../../common/constants';
import Page from '../../../components/Page.vue';
import SectionStylePanel from './SectionStylePanel.vue';
import pageStyleMixin from '@/components/WebsiteConfig/pageStyleMixin';

export default {
  components: { PageHeader, PageSeparator, FmvAvatar, Editor, vSelect, ImageUploader, SectionStylePanel },
  mixins: [pageStyleMixin],
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Custom Page' : 'Add Custom Page',

      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,

      customPage: {
        school: null,
        image_url: '',
        title: '',
        navTitle: '',
        description: '',
        metaTitle: '',
        metaDesc: '',
        isProgramPage: false,
        programCategory: null,
        shortDesc: '',
        layout: 'Layout1',
        ctaTitle: '',
        ctaLink: '',
      },
      linkedWebsite: null,
      allSchools: [],
      areSchoolsLoading: false,
      transProgramsOptions: [],
      areTransProgramsLoading: false,
      layoutOptions: [
        { text: 'Default', image: 'custom_page_layout2.png', value: 'Layout1' },
        { text: 'Default 2', image: 'custom_page_layout1.png', value: 'Layout2' },
      ],
      isLoading: false,
      school: null,
      isLogoUploading: false,
      uploadFile: { custom_page_images: null },
      uploadPercent: { custom_page_images: 0 },
      uploadCancelTokenSource: { custom_page_images: null },
      SCHOOL_PAYMENT_PLANS,
      USER_ROLES,
      LAYOUT_OPTIONS: { LAYOUT_1: 'Layout1', LAYOUT_2: 'Layout2' },
    };
  },
  validations() {
    return {
      school: {
        required: requiredIf(() => get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN)
      },
      customPage: {
        programCategory: requiredIf(() => this.customPage.isProgramPage),
        shortDesc: requiredIf(() => this.customPage.layout == this.LAYOUT_OPTIONS.LAYOUT_2),
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        {
          text: 'Custom Pages',
          to: {
            name: 'manage-custom-pages'
          }
        },
        {
          text: this.$route.params.id ? 'Edit Custom Page' : 'Add Custom Page',
          active: true
        }
      ];
    },
    heroLayout() {
      return get(this.customPage, 'layout') || 'Layout1';
    },
    isFormLoading() {
      return this.isLoading || this.isLogoUploading;
    }
  },

  methods: {
    ...mapActions('school', ['getAllSchools', 'getSchool', 'getAllTransPrograms']),
    ...mapActions('webConfig', [
      'createCustomPage',
      'updateCustomPage',
      'getCustomPage',
      'getLinkedImageUploadPresignedUrl'
    ]),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    get,

    getImgUrl(pic) {
      return require('@/assets/images/webConfig/' + pic);
    },
    async fetchTransPrograms() {
      this.areTransProgramsLoading = true;

      try {
        const response = await this.getAllTransPrograms({});
        if (response.data.length) {
          this.transProgramsOptions = response.data;
        } else {
          this.transProgramsOptions = [];
          this.customPage.programCategory = null;
        }
      } catch (e) {
        // Handle error if needed
      }

      this.areTransProgramsLoading = false;
    },
    setImage(file, image) {
      this.uploadFile[image] = file;
      this.uploadImage(image);
    },
    async onSubmit() {
      this.isLoading = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          const data = {
            title: this.customPage.title,
            navbar_title: this.customPage.navTitle,
            image_url: this.customPage.image_url,

            description: this.customPage.description,
            meta_desc: this.customPage.metaDesc,
            meta_title: this.customPage.metaTitle,
            is_program_page: this.customPage.isProgramPage,
            program_category: this.customPage.isProgramPage ? this.customPage.programCategory : null,
            layout: this.customPage.layout,
            short_description: this.customPage.shortDesc,
            cta_btn_text: this.customPage.ctaTitle,
            cta_btn_link: this.customPage.ctaLink,
            page_style: this.pageStyle,
            school:
              get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN
                ? this.school
                : this.getLoggedInUser.linked_entity.id
          };
          if (this.$route.params.id) {
            await this.updateCustomPage({
              id: this.$route.params.id,
              data
            });
            this.makeToast({ variant: 'success', msg: 'Custom Page Updated Successfully!' });
          } else {
            await this.createCustomPage(data);
            this.makeToast({ variant: 'success', msg: 'Custom Page Added Successfully!' });
          }
          setTimeout(
            () =>
              this.$router.push({
                name: 'manage-custom-pages',
                query: { school_id: this.school }
              }),
            250
          );
        } catch (_err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }
      this.isLoading = false;
    },
    async fetchSchools() {
      this.areSchoolsLoading = true;

      const response = await this.getAllSchools({
        sch_payment_plan__title: `${this.SCHOOL_PAYMENT_PLANS.ADVANCED},${this.SCHOOL_PAYMENT_PLANS.ADVANCED_AND_CHAT}`
      });
      this.allSchools = response.data;

      this.areSchoolsLoading = false;
    },

    async uploadImage(uploadType) {
      this.uploadCancelTokenSource[uploadType] = axios.CancelToken.source();
      this.isLogoUploading = uploadType === 'custom_page_images';
      try {
        const urlResp = await this.getLinkedImageUploadPresignedUrl({
          file_name: this.uploadFile[uploadType].name,
          content_type: this.uploadFile[uploadType].type
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.uploadFile[uploadType],
          config: {
            onUploadProgress: function(progressEvent) {
              this.uploadPercent[uploadType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource[uploadType].token
          }
        });
        if (uploadType === 'custom_page_images') {
          this.customPage.image_url = urlResp.upload_url.split('?')[0];
        }
      } catch (error) {
        this.uploadFile[uploadType] = null;
      }

      this.uploadCancelTokenSource[uploadType] = null;
      this.uploadPercent[uploadType] = 0;

      if (uploadType === 'custom_page_images') this.isLogoUploading = false;
    }
  },

  async mounted() {
    if (this.$route.query.school_id) {
      this.school = parseInt(this.$route.query.school_id);
    }
    if (get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN) {
      this.fetchSchools();
    }

    // Fetch program categories
    this.fetchTransPrograms();

    this.isLoading = true;
    try {
      if (this.$route.params.id) {
        const res = await this.getCustomPage(this.$route.params.id);

        this.school = res.data.school;
        this.customPage.image_url = res.data.image_url;
        this.customPage.title = res.data.title;
        this.customPage.navTitle = res.data.navbar_title;
        this.customPage.description = res.data.description;
        this.customPage.metaDesc = res.data.meta_desc;
        this.customPage.metaTitle = res.data.meta_title;
        this.customPage.isProgramPage = res.data.is_program_page;
        this.customPage.programCategory = res.data.program_category;
        this.customPage.shortDesc = res.data.short_description;
        this.customPage.layout = res.data.layout;
        this.customPage.ctaTitle = res.data.cta_btn_text;
        this.customPage.ctaLink = res.data.cta_btn_link;
        this.setPageStyle(res.data.page_style);
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      this.$router.push({
        name: 'manage-custom-pages'
      });
    }

    this.isLoading = false;
  }
};
</script>
